import React, { useEffect, useState } from "react";
import { BookOpen, Rocket, X as XIcon, ChevronLeft, ChevronRight, Mail } from "lucide-react";

/**
 * TimeWatchWelcomeDialog
 *
 * Due modalità:
 *   • mode="intro"  → popup di benvenuto con pulsanti
 *                    "Leggi la guida" / "Inizia ad usare TIME Watch"
 *   • mode="guide"  → guida sfogliabile in 4 pagine + link supporto
 *
 * Il chiamante controlla il ciclo tra i due tramite `initialMode`:
 *   - Dal pulsante "Guida" del menu / Centro Guida: initialMode="intro"
 *     oppure initialMode="guide" (a seconda dell'entry point).
 *
 * NOTA: la checkbox "Non mostrare più" è stata rimossa. L'unico popup
 * dell'onboarding con quell'opzione è ora quello nella Home. `onDontShowAgain`
 * è mantenuto per compatibilità del contratto del componente, ma non viene
 * più invocato.
 */

const GUIDE_PAGES = [
  {
    title: "Benvenuto.",
    body: [
      "TIME Watch non è nato per dirti che ore sono.",
      "È nato per aiutarti ad osservare il Tempo in modo diverso.",
    ],
  },
  {
    title: "Utilizza TIME Watch ogni giorno.",
    body: [
      "Non serve compilare tutto perfettamente.",
      "L'importante è creare l'abitudine di osservare il proprio Tempo.",
    ],
  },
  {
    title: "I tuoi dati appartengono solo a te.",
    body: [
      "TIME utilizza i tuoi dati esclusivamente per offrirti una visione più chiara del tuo Tempo.",
    ],
  },
  {
    title: "Adesso sei pronto.",
    body: [
      "Buona osservazione.",
      "TIME — Lascia il Segno",
      "È solo questione di Tempo.",
    ],
    isFinal: true,
  },
];

export default function TimeWatchWelcomeDialog({
  open,
  initialMode = "intro",
  onClose,
}) {
  const [mode, setMode] = useState(initialMode);
  const [pageIdx, setPageIdx] = useState(0);

  // Reset local state whenever the dialog is (re)opened
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setPageIdx(0);
  }, [open, initialMode]);

  if (!open) return null;

  const handleClose = () => {
    onClose && onClose();
  };

  const handleReadGuide = () => {
    setMode("guide");
    setPageIdx(0);
  };

  const handleFinishGuide = () => {
    onClose && onClose();
  };

  return (
    <div
      data-testid="tw-welcome-backdrop"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4"
      onClick={mode === "intro" ? handleClose : handleFinishGuide}
      role="dialog"
      aria-modal="true"
    >
      <div
        className="glass-card w-full max-w-lg p-7 sm:p-9 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid={mode === "intro" ? "tw-welcome-intro" : "tw-welcome-guide"}
      >
        <button
          type="button"
          onClick={mode === "intro" ? handleClose : handleFinishGuide}
          data-testid="tw-welcome-close"
          aria-label="Chiudi"
          className="absolute top-4 right-4 p-1.5 rounded-full text-white/40 hover:text-white hover:bg-white/[0.06] transition"
        >
          <XIcon className="w-4 h-4" />
        </button>

        {mode === "intro" ? (
          <IntroView
            onReadGuide={handleReadGuide}
            onStart={handleClose}
          />
        ) : (
          <GuideView
            pageIdx={pageIdx}
            setPageIdx={setPageIdx}
            onFinish={handleFinishGuide}
          />
        )}
      </div>
    </div>
  );
}

function IntroView({ onReadGuide, onStart }) {
  return (
    <div className="text-white/90">
      <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-3">
        TIME Watch
      </div>
      <h2
        data-testid="tw-welcome-title"
        className="text-2xl sm:text-3xl font-extralight text-white mb-5 leading-tight"
      >
        <span className="mr-2" aria-hidden="true">👋</span>
        Benvenuto in TIME Watch
      </h2>
      <p className="text-sm sm:text-[15px] text-white/70 font-light leading-relaxed mb-4">
        Prima di iniziare, dedica due minuti a queste brevi istruzioni.
      </p>
      <p className="text-sm sm:text-[15px] text-white/70 font-light leading-relaxed mb-4">
        TIME Watch non è un semplice strumento per misurare il Tempo. È stato
        progettato per aiutarti a osservarlo, comprenderlo e dare più valore
        alle tue giornate.
      </p>
      <p className="text-sm sm:text-[15px] text-white/70 font-light leading-relaxed mb-7">
        Per ottenere il massimo dall'esperienza, ti consigliamo di leggere
        questa breve guida.
      </p>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={onReadGuide}
          data-testid="tw-welcome-read-guide"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full border border-white/15 text-[11px] tracking-[0.24em] uppercase text-white/80 hover:text-white hover:border-white/35 transition-all"
        >
          <BookOpen className="w-3.5 h-3.5" />
          Leggi la guida
        </button>
        <button
          type="button"
          onClick={onStart}
          data-testid="tw-welcome-start"
          className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all"
        >
          <Rocket className="w-3.5 h-3.5" />
          Inizia ad usare TIME Watch
        </button>
      </div>
    </div>
  );
}

function GuideView({ pageIdx, setPageIdx, onFinish }) {
  const page = GUIDE_PAGES[pageIdx];
  const isFirst = pageIdx === 0;
  const isLast = pageIdx === GUIDE_PAGES.length - 1;

  return (
    <div className="text-white/90">
      <div className="text-[10px] tracking-[0.32em] uppercase text-emerald-300/80 mb-3">
        Guida rapida · Pagina {pageIdx + 1} / {GUIDE_PAGES.length}
      </div>

      <div data-testid={`tw-guide-page-${pageIdx + 1}`} className="min-h-[220px]">
        <h3 className="text-2xl sm:text-3xl font-extralight text-white mb-6 leading-tight">
          {page.title}
        </h3>
        <div className="space-y-3">
          {page.body.map((line, i) => (
            <p
              key={i}
              className={
                page.isFinal && i > 0
                  ? "text-sm sm:text-[15px] text-emerald-200/90 font-light leading-relaxed"
                  : "text-sm sm:text-[15px] text-white/70 font-light leading-relaxed"
              }
            >
              {line}
            </p>
          ))}
        </div>

        {isLast && (
          <div
            data-testid="tw-guide-support"
            className="mt-7 pt-5 border-t border-white/5 text-[12px] text-white/45 leading-relaxed"
          >
            <div className="flex items-center gap-2 mb-1">
              <Mail className="w-3.5 h-3.5 text-white/40" />
              <span className="tracking-wide">Hai bisogno di aiuto?</span>
            </div>
            <div>
              Contattaci:{" "}
              <a
                href="mailto:info@timeinside.it"
                data-testid="tw-guide-support-email"
                className="text-emerald-300 hover:text-emerald-200 transition-colors"
              >
                info@timeinside.it
              </a>
            </div>
          </div>
        )}
      </div>

      {/* Dot pagination */}
      <div className="flex items-center justify-center gap-2 mt-7 mb-6" role="tablist">
        {GUIDE_PAGES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => setPageIdx(i)}
            aria-label={`Vai a pagina ${i + 1}`}
            data-testid={`tw-guide-dot-${i + 1}`}
            className={[
              "h-1.5 rounded-full transition-all",
              i === pageIdx
                ? "w-6 bg-emerald-400"
                : "w-1.5 bg-white/20 hover:bg-white/40",
            ].join(" ")}
          />
        ))}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => setPageIdx((v) => Math.max(0, v - 1))}
          disabled={isFirst}
          data-testid="tw-guide-prev"
          className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-[0.22em] uppercase text-white/50 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Indietro
        </button>

        {isLast ? (
          <button
            type="button"
            onClick={onFinish}
            data-testid="tw-guide-finish"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition-all"
          >
            <Rocket className="w-3.5 h-3.5" />
            Inizia ad usare TIME Watch
          </button>
        ) : (
          <button
            type="button"
            onClick={() => setPageIdx((v) => Math.min(GUIDE_PAGES.length - 1, v + 1))}
            data-testid="tw-guide-next"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-white/15 text-[11px] tracking-[0.22em] uppercase text-white/80 hover:text-white hover:border-white/35 transition-all"
          >
            Avanti
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );
}
